import React, { useState, useEffect, useMemo } from 'react';
import FeedbackService from './FeedbackService';
import filterFieldsForList from '../common/functions/filterFieldsForList';
import schema from './jsonSchema';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import ReactECharts from 'echarts-for-react';
import Background from 'components/common/Background';
import corner1 from 'assets/img/icons/spot-illustrations/corner-1.png';
import corner3 from 'assets/img/icons/spot-illustrations/corner-3.png';
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function bucket(score) {
  if (score < 2) return 'Negative';
  if (score < 4) return 'Neutral';
  return 'Positive';
}

function scoreToBadge(score) {
  if (score < 2.5) return { text: 'Needs Improvement', tone: 'red' };
  if (score < 4) return { text: 'Good Contributor', tone: 'amber' };
  return { text: 'Highly Appreciated', tone: 'green' };
}
function EmployeeGaugeChart({ items }) {
  const avg = useMemo(() => {
    if (!items?.length) return 0;
    return (
      items.reduce((a, x) => a + (x.sentimentScore || 0), 0) / items.length
    );
  }, [items]);

  const pct = clamp((avg / 5) * 100, 0, 100);
  const badge = scoreToBadge(avg);

  const option = {
    tooltip: { show: false },
    series: [
      {
        type: 'gauge',
        startAngle: 210,
        endAngle: -30,
        min: 0,
        max: 100,
        progress: {
          show: true,
          roundCap: true,
          itemStyle: { color: '#00d27a' }
        },
        axisLine: { lineStyle: { width: 30 } },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        pointer: { show: false },
        anchor: { show: false },
        detail: {
          formatter: `${avg.toFixed(1)}/5`,
          fontSize: 26,
          color: '#2c7be5',
          offsetCenter: [0, '10%']
        },
        data: [{ value: pct }]
      }
    ]
  };

  return (
    <div
      style={{
        borderRadius: 16,
        padding: 16,
        boxShadow: '0 1px 6px rgba(0,0,0,.06)'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          My Feedback Sentiment
        </h3>
        <span style={{ fontSize: 12, color: badge.color }}>{badge.text}</span>
      </div>
      <div style={{ height: 220 }}>
        <ReactECharts
          option={option}
          notMerge
          lazyUpdate
          style={{ height: '100%', width: '100%' }}
        />
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
        Average from {items.length} feedbacks
      </div>
    </div>
  );
}
function TeamBarsChart({ items }) {
  const counts = useMemo(() => {
    const b = { Positive: 0, Neutral: 0, Negative: 0 };
    for (const f of items) b[bucket(f.sentimentScore)]++;
    return b;
  }, [items]);

  const option = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: 80, right: 20, top: 20, bottom: 20 },
    xAxis: { type: 'value', boundaryGap: [0, 0.1] },
    yAxis: { type: 'category', data: ['Positive', 'Neutral', 'Negative'] },
    series: [
      {
        type: 'bar',
        data: [counts.Positive, counts.Neutral, counts.Negative],
        barWidth: 30,
        itemStyle: {
          borderRadius: 6,
          color: params => ['#22c55e', '#f59e0b', '#ef4444'][params.dataIndex]
        },
        label: { show: true, position: 'right' }
      }
    ]
  };

  return (
    <div
      style={{
        borderRadius: 16,
        padding: 16,
        boxShadow: '0 1px 6px rgba(0,0,0,.06)'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 8
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          Feedback Sentiment 
        </h3>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          Distribution by ranking
        </span>
      </div>
      <div style={{ height: 225 }}>
        <ReactECharts
          option={option}
          notMerge
          lazyUpdate
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    </div>

  );
}
const FeedbackWidget = () => {
  const [data, setData] = useState();
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');
  const [noDataToShow, setNoDataToShow] = useState(true);
  const [feedbacks, setFeedbacks] = useState();
  const [showEmployeeGaugeChart, setShowEmployeeGaugeChart] = useState(false);
  const navigate = useNavigate();
  const buildLabel = sentimentScore => {
    return sentimentScore === null || sentimentScore < 3
      ? 'negative'
      : sentimentScore < 4
      ? 'neutral'
      : 'positive';
  };
  useEffect(() => {
    const jsonSchema = schema;
    const fieldsForList = filterFieldsForList(jsonSchema);
    setColumns(fieldsForList);

    FeedbackService.retrieveAllFeedbackByAccount()
      .then(data => {
        console.log('Sarasa', data);
        const feedbacks = data.map(f => ({
          id: f.identifier,
          sentimentScore: f.sentimentScore === null ? 0 : f.sentimentScore,
          label: buildLabel(f.sentimentScore)
        }));
        setFeedbacks(feedbacks);
        setShowEmployeeGaugeChart(feedbacks.length > 0);
        console.log('FeedbackWidget', feedbacks);
      })
      .catch(error => setError({ error: error.message }));
  }, []);

  return (
    <>
      {showEmployeeGaugeChart && (
        <Row className="g-3">
          <Col xxl={8}>
            <Card>
              <Background image={corner1} className="rounded-soft bg-card" />
              <Card.Body className="p-4">
                <TeamBarsChart items={feedbacks} />
              </Card.Body>
            </Card>
          </Col>
          <Col xxl={4}>
            <Card className='mb-3'>
              <Background image={corner3} className="rounded-soft bg-card" />
              <Card.Body>
                <EmployeeGaugeChart items={feedbacks} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default FeedbackWidget;
